import dayjs from 'dayjs';
import {
  guaranteesApplicationStatusConfig,
  guaranteesClientText,
} from '~/modules/guarantees/form/client/constants.ts';
import {
  GuaranteesApplicationStatus,
  guaranteesApplicationStatuses,
  type TGuaranteesTimelineEvent,
} from '~/store/guarantees/types.ts';
import { normalizeGuaranteesApplicationStatus } from '~/store/guarantees/utils.ts';
import type { TStatusFlowNode } from './types.ts';

export const isKnownGuaranteesStatus = (
  status?: GuaranteesApplicationStatus | string | null
): status is GuaranteesApplicationStatus =>
  guaranteesApplicationStatuses.includes(status as GuaranteesApplicationStatus);

export const getStatusLabel = (status: GuaranteesApplicationStatus | string): string => {
  if (isKnownGuaranteesStatus(status)) {
    return guaranteesApplicationStatusConfig[status].label;
  }

  return String(status || guaranteesClientText.emptyValue);
};

export const localizeStatusEnumText = (value?: string): string =>
  guaranteesApplicationStatuses.reduce(
    (text, status) => text.replaceAll(status, getStatusLabel(status)),
    value ?? ''
  );

export const getStatusColor = (status: GuaranteesApplicationStatus | string) =>
  guaranteesApplicationStatusConfig[normalizeGuaranteesApplicationStatus(status)].color;

export const getFormattedDateTime = (value?: string): string => {
  if (!value || !dayjs(value).isValid()) {
    return guaranteesClientText.emptyValue;
  }

  return dayjs(value).format('DD.MM.YYYY HH:mm');
};

export const buildStatusFlowNodes = (
  events: TGuaranteesTimelineEvent[]
): TStatusFlowNode[] => {
  const firstEvent = events[0];

  if (!firstEvent) {
    return [];
  }

  const firstStatus = firstEvent.statusFrom || firstEvent.statusTo;
  const nodes: TStatusFlowNode[] = firstStatus ? [{ status: firstStatus, event: firstEvent }] : [];

  events.forEach((event) => {
    const nextStatus = event.statusTo;

    if (!nextStatus) {
      return;
    }

    const previousNode = nodes[nodes.length - 1];

    if (previousNode && previousNode.status === nextStatus) {
      previousNode.event = event;
      return;
    }

    nodes.push({
      status: nextStatus,
      event,
    });
  });

  return nodes;
};
